// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  PRODUCT_FIELD_TYPE_DATA,
  ProductFieldType,
} from '@/constants/product-field-type';
import { ItemField } from '@/items/api/types';
import {
  createFieldMap,
  getUniqueFieldLabels,
} from '@/items/components/merge-item/utilities/merge-utilities';
import { Box, Typography } from '@mui/material';

interface Props {
  primaryFields: ItemField[];
  selectedFields: ItemField[];
}

function getFieldTypeLabel(type: ProductFieldType): string {
  return PRODUCT_FIELD_TYPE_DATA[type].fieldTypeLabel;
}

export function ItemFieldsRow(props: Props) {
  const sortedLabels = getUniqueFieldLabels(
    props.primaryFields,
    props.selectedFields
  );
  const allFields = createFieldMap([
    ...props.primaryFields,
    ...props.selectedFields,
  ]);

  if (sortedLabels.length === 0) {
    return null;
  }

  return (
    <Box
      display="grid"
      gridTemplateColumns="2fr 2fr 2fr 3fr"
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 2,
        py: 2,
        alignItems: 'flex-start',
      }}
    >
      <Typography variant="body2">Item fields</Typography>

      <Box>
        <Box component="ul" sx={{ m: 0, pl: 0, listStyleType: 'disc' }}>
          {sortedLabels.map((labelLower, index) => {
            const primaryField = props.primaryFields.find(
              (f) => f.label.toLowerCase() === labelLower
            );

            return primaryField ? (
              <Box component="li" key={index} sx={{ mb: 1, ml: 5.5 }}>
                <Typography variant="body2">
                  {primaryField.label}{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    ({getFieldTypeLabel(primaryField.type)})
                  </Typography>
                </Typography>
              </Box>
            ) : (
              <Box key={index} sx={{ mb: 1, minHeight: '24px' }}>
                {/* Empty space for alignment */}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box>
        <Box component="ul" sx={{ m: 0, pl: 0, listStyleType: 'disc' }}>
          {sortedLabels.map((labelLower, index) => {
            const selectedField = props.selectedFields.find(
              (f) => f.label.toLowerCase() === labelLower
            );

            return selectedField ? (
              <Box component="li" key={index} sx={{ mb: 1, ml: 5.5 }}>
                <Typography variant="body2">
                  {selectedField.label}{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    ({getFieldTypeLabel(selectedField.type)})
                  </Typography>
                </Typography>
              </Box>
            ) : (
              <Box key={index} sx={{ mb: 1, minHeight: '24px' }}>
                {/* Empty space for alignment */}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box>
        <Box component="ul" sx={{ m: 0, pl: 0, listStyleType: 'disc' }}>
          {sortedLabels.map((labelLower, index) => {
            const mergedField = allFields.get(labelLower);

            return mergedField ? (
              <Box component="li" key={index} sx={{ mb: 1, ml: 2 }}>
                <Typography variant="body2">
                  {mergedField.label}{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    ({getFieldTypeLabel(mergedField.type)})
                  </Typography>
                </Typography>
              </Box>
            ) : (
              <Box key={index} sx={{ mb: 1, minHeight: '24px' }}>
                {/* Empty space for alignment */}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
